// Thành phần dùng chung.
//
// Ở đây CHỈ còn logic và class Tailwind thuần. Trước khi chuyển sang
// Tailwind có một file CSS 500 dòng định nghĩa `.btn`, `.opt`, `.card`…
// bằng những giá trị px chép từ mockup. Những giá trị đó sai: khung mockup
// rộng 258px, điện thoại thật rộng 390–430px, nên `font-size: 11.5px` dán
// nguyên vào là nhỏ như chữ chú thích dưới ảnh.
//
// Nên giờ: class Tailwind thuần, không có giá trị px nào tự đặt. Muốn chỉnh
// thì dùng thang chuẩn (text-sm, p-4, rounded-lg, gap-3…) và để trình duyệt
// lo phần còn lại.

import { Icon, type IconName } from './Icon'

/** Màu thanh tiến trình theo tỉ lệ đúng.
 *
 *  Ngưỡng lấy từ thiết kế: <50% đỏ, 50–79% xanh dương, ≥80% xanh lá. Dùng
 *  chung hàm này ở Home lẫn Tiến độ — nếu mỗi màn tự quyết thì một đề mục
 *  sẽ hiện màu khác nhau giữa hai màn, và người dùng tưởng dữ liệu lệch.
 *
 *  `accuracy = -1` nghĩa là CHƯA LÀM — trả màu trung tính, không phải đỏ.
 *  Tô đỏ một đề mục người dùng chưa đụng tới là phán xét về năng lực của họ
 *  dựa trên dữ liệu không tồn tại.
 */
export function accTone(accuracy: number): { text: string; bar: string } {
  if (accuracy < 0) return { text: 'text-ink-soft', bar: 'bg-accent' }
  if (accuracy >= 80) return { text: 'text-good', bar: 'bg-good' }
  if (accuracy >= 50) return { text: 'text-ink-soft', bar: 'bg-accent' }
  return { text: 'text-bad', bar: 'bg-bad' }
}

/** Hiển thị tỉ lệ đúng. -1 → "chưa làm" thay vì "0%". */
export function accText(accuracy: number): string {
  return accuracy < 0 ? '—' : Math.round(accuracy) + '%'
}

/** Thanh tiến trình. `percent` = -1 nghĩa là chưa làm → vẽ rỗng.
 *
 *  `decorative` dành cho thanh trang trí — không phải tiến trình thật. Màn
 *  Welcome vẽ ba thanh 80/55/68 làm hoạ hình giới thiệu phạm vi nội dung; để
 *  mặc định thì trình đọc màn hình đọc thành "80% hoàn thành", tức thông tin
 *  sai về tiến độ người đọc đang có. `decorative` thì ẩn hẳn khỏi cây
 *  accessibility, đúng như nó không mang thông tin gì. */
export function Bar({
  percent,
  tone = 'bg-accent',
  className = '',
  decorative = false,
}: {
  percent: number
  tone?: string
  className?: string
  decorative?: boolean
}) {
  const w = percent < 0 ? 0 : Math.max(0, Math.min(100, percent))
  return (
    <div
      className={`bar ${className}`}
      // aria: thanh tiến trình thật thì cần nhãn — trình đọc màn hình không đọc
      // được bằng màu. Thanh trang trí thì bỏ hẳn, đọc "80%" chỉ là nói sai.
      {...(decorative
        ? { 'aria-hidden': 'true' as const }
        : {
            role: 'progressbar' as const,
            'aria-valuenow': Math.round(w),
            'aria-valuemin': 0,
            'aria-valuemax': 100,
          })}
    >
      <div className={`bar-fill ${tone}`} style={{ width: w + '%' }} />
    </div>
  )
}

type TabId = 'home' | 'practice' | 'exam' | 'chart'

export function NavBar({ active, onGo }: { active: TabId; onGo: (t: TabId) => void }) {
  const tabs: Array<{ id: TabId; label: string; icon: IconName }> = [
    { id: 'home', label: 'Trang chủ', icon: 'home' },
    { id: 'practice', label: 'Ôn luyện', icon: 'practice' },
    { id: 'exam', label: 'Thi thử', icon: 'exam' },
    { id: 'chart', label: 'Tiến độ', icon: 'chart' },
  ]
  return (
    <nav className="flex items-stretch justify-around border-t border-ink/10 bg-surface px-1 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
      {tabs.map((t) => (
        <button
          key={t.id}
          onClick={() => onGo(t.id)}
          className={
            'flex flex-1 cursor-pointer flex-col items-center gap-1 rounded-lg py-1 text-xs font-medium transition-colors ' +
            (active === t.id ? 'text-accent' : 'text-ink-faint hover:text-ink-soft')
          }
          // aria-current đánh dấu tab hiện tại cho trình đọc màn hình.
          aria-current={active === t.id ? 'page' : undefined}
        >
          <Icon name={t.icon} size={20} />
          {t.label}
        </button>
      ))}
    </nav>
  )
}

/** Nút quay lại — icon mũi tên trái trong hộp tròn, vuông với nút tab. */
export function BackButton({ onClick, label = 'Quay lại' }: { onClick: () => void; label?: string }) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      className="-ml-2 cursor-pointer rounded-full p-2 text-ink transition-colors hover:bg-canvas-2"
    >
      <Icon name="back" size={20} />
    </button>
  )
}

/** Thanh trên: quay lại + tiêu đề + phần bên phải (chip, nút). */
export function TopBar({
  title,
  onBack,
  right,
}: {
  title: string
  onBack?: () => void
  right?: React.ReactNode
}) {
  return (
    <div className="mb-4 flex items-center gap-1">
      {onBack && <BackButton onClick={onBack} />}
      <h2 className="text-base font-semibold">{title}</h2>
      {right && <div className="ml-auto">{right}</div>}
    </div>
  )
}
